'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Edit3, LogOut, ShieldCheck } from 'lucide-react'
import Avatar from 'boring-avatars'
import { formatImageURL, isLoyaltyActive } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { useProfileNav } from '@/lib/commerce/hooks'

function ProfileHero({ customer }: { customer: any }) {
  const pathname = usePathname()
  const initial = (customer?.firstName?.[0] || customer?.email?.[0] || '?').toUpperCase()
  const joinedAt = customer?.createdAt ? new Date(customer.createdAt) : null
  const joinedLabel = joinedAt
    ? `Гишүүн · ${joinedAt.getFullYear()} · ${joinedAt.getMonth() + 1} сар`
    : 'Гишүүн'

  const nameNode = (
    <>
      <span className="truncate">{customer?.firstName || customer?.email?.split('@')[0] || 'Хэрэглэгч'}</span>
      {customer?.isVerified && (
        <span
          className="inline-flex items-center gap-1 px-2 h-5 sm:h-6 rounded-full text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.16em]"
          style={{ background: 'rgba(16,185,129,0.18)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' }}
        >
          <ShieldCheck className="h-2.5 w-2.5" strokeWidth={2.5} />
          Verified
        </span>
      )}
    </>
  )

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8">
      <div
        className="relative rounded-2xl overflow-hidden mt-4 sm:mt-6 p-5 sm:p-0 sm:h-44"
        style={{
          background: 'radial-gradient(800px 400px at 80% 0%, rgba(255,255,255,0.08), transparent 60%), radial-gradient(600px 400px at 0% 100%, rgba(255,255,255,0.04), transparent 60%), #0c0c10',
          border: '1px solid rgba(255,255,255,0.13)',
        }}
      >
        <div className="sm:hidden flex items-center gap-4">
          <div className="relative shrink-0">
            <div
              className="w-16 h-16 rounded-full inline-flex items-center justify-center text-[22px] font-semibold overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #2a2a30, #08080a)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.18)',
                fontFamily: 'Gilroy, Inter, sans-serif',
              }}
            >
              {customer?.avatar ? (
                <img src={formatImageURL(customer.avatar)} alt="profile" className="w-full h-full object-cover" />
              ) : customer?.email ? (
                <Avatar size={64} variant="marble" name={customer.email} />
              ) : (
                initial
              )}
            </div>
            <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-400" style={{ border: '2px solid #0c0c10' }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-1" style={{ color: '#a8a8b3' }}>
              {joinedLabel}
            </div>
            <div
              className="text-[18px] font-semibold leading-tight text-white inline-flex items-center gap-2 flex-wrap"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}
            >
              {nameNode}
            </div>
            {customer?.email && (
              <div className="text-[12px] mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.6)' }}>{customer.email}</div>
            )}
          </div>
          <Link
            href={navigateUrl('/settings', pathname)}
            aria-label="Профайл засах"
            className="shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center transition-colors hover:bg-white/10"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.14)', color: 'white' }}
          >
            <Edit3 className="h-3.5 w-3.5" />
          </Link>
        </div>

        <Link
          href={navigateUrl('/settings', pathname)}
          className="hidden sm:inline-flex absolute top-4 right-4 h-9 px-4 rounded-full items-center gap-2 text-[12px] font-medium backdrop-blur-md transition-colors hover:bg-white/20"
          style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)', color: 'white' }}
        >
          <Edit3 className="h-3 w-3" /> Профайл засах
        </Link>
      </div>

      <div className="hidden sm:flex relative z-10 flex-col sm:flex-row sm:items-end sm:justify-between gap-6 -mt-7 px-2 pb-8">
        <div className="flex items-end gap-5">
          <div className="relative shrink-0">
            <div
              className="w-24 h-24 rounded-full inline-flex items-center justify-center text-[32px] font-semibold overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, #2a2a30, #08080a)',
                color: 'white',
                border: '4px solid #08080a',
                fontFamily: 'Gilroy, Inter, sans-serif',
                boxShadow: '0 12px 30px rgba(0,0,0,0.4)',
              }}
            >
              {customer?.avatar ? (
                <img src={formatImageURL(customer.avatar)} alt="profile" className="w-full h-full object-cover" />
              ) : customer?.email ? (
                <Avatar size={88} variant="marble" name={customer.email} />
              ) : (
                initial
              )}
            </div>
            <span className="absolute bottom-1 right-1 w-3.5 h-3.5 rounded-full bg-emerald-400" style={{ border: '3px solid #08080a' }} />
          </div>
          <div className="pb-2 min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] mb-1" style={{ color: '#a8a8b3' }}>
              {joinedLabel}
            </div>
            <h1
              className="font-semibold tracking-[-0.02em] leading-none text-white inline-flex items-center gap-2.5 flex-wrap"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(24px, 4vw, 40px)' }}
            >
              {nameNode}
            </h1>
            {customer?.email && (
              <div className="mt-2 text-[13px] truncate" style={{ color: 'rgba(255,255,255,0.6)' }}>{customer.email}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function ProfileTabs({ vendor }: { vendor: any }) {
  const { accountItems, logoutItem } = useProfileNav()
  const visibleItems = accountItems.filter(item => item.key !== 'loyalty' || isLoyaltyActive(vendor))
  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
          {visibleItems.map(item => (
            <Link
              key={item.key}
              href={item.url}
              className="h-12 px-1 mr-6 -mb-px border-b-2 text-[14px] transition-colors shrink-0 inline-flex items-center"
              style={{
                borderColor: item.isActive ? 'white' : 'transparent',
                color: item.isActive ? 'white' : 'rgba(255,255,255,0.6)',
                fontWeight: item.isActive ? 600 : 400,
              }}
            >
              {item.name}
            </Link>
          ))}
        </div>
        {logoutItem && (
          <Link
            href={logoutItem.url}
            className="hidden sm:inline-flex h-9 px-4 rounded-full text-[12.5px] font-medium items-center gap-2 shrink-0 transition-colors hover:bg-rose-500/10"
            style={{ color: '#fda4af' }}
          >
            <LogOut className="h-3.5 w-3.5" />
            Гарах
          </Link>
        )}
      </div>
    </div>
  )
}

export default function ProfileLayout({ children, customer, vendor }: { children: React.ReactNode, customer: any, vendor: any }) {
  return (
    <div>
      <ProfileHero customer={customer} />
      <ProfileTabs vendor={vendor} />
      <div>{children}</div>
    </div>
  )
}
